import { useEffect, useRef, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "./UI";
import { Bell, Camera, Check, Loader2, Trash2, X } from "lucide-react";
import ReactCrop from "react-image-crop";
import { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { SettingsAPI } from "api/settings/service";
import { useAlert } from "components/UI_components/Alert";
import { useNotifications } from "utils/Notifications/NotificationContext";

const mapProfile = (profile = {}) => {
  profile = profile || {};

  return {
    firstName: profile.first_name,
    lastName: profile.last_name,
    email: profile.user_email,
    phone: profile.contact_number,
    language: profile.language,
    userpic: profile.userpic,
    profession: profile.profession,
    address_id: profile.address_id,
    id: profile.id,
    timezone: "Asia/Kolkata",
  };
};

const professionOptions = [
  "Contractor",
  "Supervisor",
  "Manager",
  "Architect",
  "Engineer",
  "Vendor",
  "Employee",
  "Others",
];

export function ProfileSection() {
  const { addAlert } = useAlert();
  const { notificationsEnabled, setNotificationsEnabled } = useNotifications();
  const [profile, setProfile] = useState(
    mapProfile(JSON.parse(localStorage.getItem("user") || "{}")) || {},
  );
  const [address, setAddress] = useState({});
  const [hasAddress, setHasAddress] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [tempImageSrc, setTempImageSrc] = useState(null);
  const [tempImageType, setTempImageType] = useState("image/jpeg");
  const [tempImageName, setTempImageName] = useState("profile-photo.jpg");
  const [isRemovingPhoto, setIsRemovingPhoto] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const res = await SettingsAPI.getUserProfile();
        const profileData = mapProfile(res.data);
        setProfile(profileData);
        localStorage.setItem("user", JSON.stringify(res.data));

        if (profileData.address_id) {
          try {
            const addressRes = await SettingsAPI.getAddress(
              profileData.address_id,
            );
            if (addressRes.status && addressRes.data) {
              setAddress({
                street_address: addressRes.data.street_address || "",
                city: addressRes.data.city || "",
                state: addressRes.data.state || "",
                pincode: addressRes.data.pincode || "",
                country: "India",
              });
              setHasAddress(true);
            }
          } catch (error) {
            console.error("Error fetching address:", error);
          }
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfileData();
  }, []);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // 1. Update Profile Details
      const profilePayload = {
        first_name: profile.firstName,
        last_name: profile.lastName,
        contact_number: profile.phone,
      };

      if (profile.profession !== "owner") {
        profilePayload.profession = profile.profession;
      }

      await SettingsAPI.updateUserDetails(profile.id, profilePayload);

      // 2. Update or Create Address
      const addressPayload = {
        street_address: address.street_address,
        city: address.city,
        state: address.state,
        pincode: address.pincode,
        country: "India",
      };

      if (hasAddress) {
        await SettingsAPI.updateAddress(profile.address_id, addressPayload);
      } else if (Object.values(address).some((v) => v && v !== "India")) {
        await SettingsAPI.createAddress(addressPayload);
      }

      addAlert("Your changes have been saved successfully.", "success");
    } catch (error) {
      console.error("Error saving profile:", error);
      addAlert(
        error.message || "Failed to save changes. Please try again.",
        "destructive",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationPreferenceChange = (enabled) => {
    setNotificationsEnabled(enabled);
    addAlert(
      enabled ? "Notifications turned on." : "Notifications turned off.",
      "success",
    );
  };

  const handleImageClick = () => {
    document.getElementById("profile-image-input").click();
  };

  const getCropFileMeta = (file) => {
    const baseName = (file.name || "profile-photo").replace(/\.[^/.]+$/, "");
    if (file.type === "image/png" || file.type === "image/jpeg") {
      return {
        type: file.type,
        name: file.name || "profile-photo.jpg",
      };
    }

    return {
      type: "image/png",
      name: `${baseName}.png`,
    };
  };

  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append("userpic", file);

    try {
      const res = await SettingsAPI.updateUserDetails(profile.id, formData);
      if (res.status) {
        const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
        const updatedUser = { ...existingUser, ...(res.data || {}) };
        setProfile((prev) => ({ ...prev, userpic: updatedUser.userpic }));
        localStorage.setItem("user", JSON.stringify(updatedUser));
        window.dispatchEvent(
          new CustomEvent("userProfileUpdated", { detail: updatedUser }),
        );
        addAlert("Profile image updated successfully.", "success");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      addAlert(
        error.message || "Failed to upload image. Please try again.",
        "destructive",
      );
    }
  };

  const handleRemoveProfilePicture = async () => {
    if (!profile?.id || !profile?.userpic || isRemovingPhoto) return;

    setIsRemovingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("userpic", "");
      const res = await SettingsAPI.updateUserDetails(profile.id, formData);

      if (res?.status) {
        setProfile((prev) => ({ ...prev, userpic: null }));
        const existingUser = JSON.parse(localStorage.getItem("user") || "{}");
        const updatedUser = {
          ...existingUser,
          ...(res.data || {}),
          userpic: null,
        };
        localStorage.setItem(
          "user",
          JSON.stringify(updatedUser),
        );
        window.dispatchEvent(
          new CustomEvent("userProfileUpdated", { detail: updatedUser }),
        );
        addAlert("Profile photo removed.", "success");
      }
    } catch (error) {
      console.error("Error removing profile image:", error);
      addAlert(
        error.message || "Failed to remove profile photo. Please try again.",
        "destructive",
      );
    } finally {
      setIsRemovingPhoto(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        addAlert("Image size should be less than 2MB.", "destructive");
        return;
      }
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        addAlert(
          "Please upload a valid image (JPG, PNG or GIF).",
          "destructive",
        );
        return;
      }

      const cropFileMeta = getCropFileMeta(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setTempImageType(cropFileMeta.type);
        setTempImageName(cropFileMeta.name);
        setTempImageSrc(reader.result);
        setIsCropperOpen(true);
      };
      reader.readAsDataURL(file);
      if (e.target) {
        e.target.value = "";
      }
    }
  };

  const createImage = (url) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener("load", () => resolve(image));
      image.addEventListener("error", (error) => reject(error));
      image.src = url;
    });

  const getCroppedImg = async (
    imageSrc,
    pixelCrop,
    mimeType = "image/jpeg",
  ) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const naturalWidth = image.naturalWidth || image.width;
    const naturalHeight = image.naturalHeight || image.height;
    const renderedRect = imgRef.current?.getBoundingClientRect();
    const renderedWidth = renderedRect?.width || imgRef.current?.width || image.width;
    const renderedHeight =
      renderedRect?.height || imgRef.current?.height || image.height;

    const scaleX = naturalWidth / renderedWidth;
    const scaleY = naturalHeight / renderedHeight;

    const outputWidth = Math.max(1, Math.floor(pixelCrop.width));
    const outputHeight = Math.max(1, Math.floor(pixelCrop.height));
    const sx = Math.max(
      0,
      Math.min(naturalWidth - 1, Math.floor(pixelCrop.x * scaleX)),
    );
    const sy = Math.max(
      0,
      Math.min(naturalHeight - 1, Math.floor(pixelCrop.y * scaleY)),
    );
    const sourceWidth = Math.max(
      1,
      Math.min(naturalWidth - sx, Math.floor(pixelCrop.width * scaleX)),
    );
    const sourceHeight = Math.max(
      1,
      Math.min(naturalHeight - sy, Math.floor(pixelCrop.height * scaleY)),
    );

    canvas.width = outputWidth;
    canvas.height = outputHeight;

    ctx.drawImage(
      image,
      sx,
      sy,
      sourceWidth,
      sourceHeight,
      0,
      0,
      outputWidth,
      outputHeight,
    );

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        mimeType,
        0.92,
      );
    });
  };

  const handleCropCancel = () => {
    setIsCropperOpen(false);
    setCompletedCrop(null);
    setTempImageSrc(null);
    setCrop(undefined);
  };

  const handleCropConfirm = async () => {
    try {
      const normalizeToPixelCrop = (cropValue) => {
        if (!cropValue) return null;

        if (cropValue.unit === "%") {
          const renderedRect = imgRef.current?.getBoundingClientRect();
          const renderedWidth = renderedRect?.width || imgRef.current?.width || 0;
          const renderedHeight =
            renderedRect?.height || imgRef.current?.height || 0;
          if (!renderedWidth || !renderedHeight) return null;

          return {
            unit: "px",
            x: (cropValue.x / 100) * renderedWidth,
            y: (cropValue.y / 100) * renderedHeight,
            width: (cropValue.width / 100) * renderedWidth,
            height: (cropValue.height / 100) * renderedHeight,
          };
        }

        return cropValue;
      };

      const pixelCrop = normalizeToPixelCrop(completedCrop || crop);
      if (!tempImageSrc || !pixelCrop || !pixelCrop.width || !pixelCrop.height)
        return;

      const blob = await getCroppedImg(tempImageSrc, pixelCrop, tempImageType);
      const croppedFile = new File([blob], tempImageName, {
        type: tempImageType,
      });
      if (croppedFile.size > 2 * 1024 * 1024) {
        addAlert("Image size should be less than 2MB.", "destructive");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setProfile((prev) => ({ ...prev, userpic: reader.result }));
        handleCropCancel();
      };
      reader.readAsDataURL(croppedFile);

      await handleImageUpload(croppedFile);
    } catch (err) {
      console.error("Cropping failed", err);
      addAlert("Failed to apply crop. Please try again.", "destructive");
    }
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    if (!width || !height) return;

    // Start with a centered square crop that fits fully inside the visible image.
    // This keeps the full image context visible initially, similar to WhatsApp's crop UX.
    const initialCrop = centerCrop(
      makeAspectCrop({ unit: "%", width: 90 }, 1, width, height),
      width,
      height,
    );

    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  };

  return (
    <div
      className="space-y-6"
      style={{
        scrollbarWidth: "none",
      }}
    >
      <div>
        <h2 className="text-2xl font-semibold text-primary-text">Profile</h2>
        <p className="text-text-gray mt-1">
          Manage your personal information and preferences.
        </p>
      </div>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Profile Photo</CardTitle>
          <CardDescription>
            This will be displayed across SiteMaster.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6">
            <input
              type="file"
              id="profile-image-input"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
            <div className="relative">
              <Avatar className="h-20 w-20">
                {profile.userpic ? (
                  <AvatarImage src={profile.userpic} />
                ) : (
                  <AvatarFallback className="bg-sm-primary text-white text-xl">
                    {(profile.firstName?.charAt(0) || "").toUpperCase() +
                      (profile.lastName?.charAt(0) || "").toUpperCase()}
                  </AvatarFallback>
                )}
              </Avatar>
              <button
                onClick={handleImageClick}
                className="absolute bottom-0 right-0 p-1.5 bg-sm-primary text-white rounded-full hover:bg-sm-hover transition-colors shadow-sm"
              >
                <Camera className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleImageClick}
                  variant="outline"
                  size="sm"
                  type="button"
                  className="h-[30px] px-3 text-xs"
                >
                  Upload Photo
                </Button>
                <Button
                  onClick={handleRemoveProfilePicture}
                  variant="outline"
                  size="icon"
                  type="button"
                  disabled={!profile.userpic || isRemovingPhoto}
                  className="h-[30px] w-[30px] text-red-600 hover:text-red-700"
                  aria-label="Remove profile picture"
                  title="Remove profile picture"
                >
                  {isRemovingPhoto ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    <Trash2 className="h-3 w-3" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-text-gray">
                JPG, PNG or GIF. Max 2MB.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Personal Information</CardTitle>
          <CardDescription>Update your personal details here.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={profile.firstName}
                onChange={(e) =>
                  setProfile({ ...profile, firstName: e.target.value })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={profile.lastName}
                onChange={(e) =>
                  setProfile({ ...profile, lastName: e.target.value })
                }
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              value={profile.email}
              disabled
              className="bg-gray-bg/50"
            />
            <p className="text-xs text-text-gray">
              Email cannot be changed. Contact support if needed.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                value={profile.phone}
                onChange={(e) =>
                  setProfile({ ...profile, phone: e.target.value })
                }
              />
            </div>
            {profile.profession !== "owner" && (
              <div className="space-y-2">
                <Label htmlFor="profession">Profession</Label>
                <Select
                  value={profile.profession?.toLowerCase()}
                  onValueChange={(v) => setProfile({ ...profile, profession: v })}
                  disabled={profile.profession === "owner"}
                >
                  <SelectTrigger
                    id="profession"
                    className={
                      profile.profession === "owner" ? "bg-gray-bg/50" : ""
                    }
                  >
                    <SelectValue placeholder="Select Profession" />
                  </SelectTrigger>
                  <SelectContent>
                    {profile.profession === "owner" && (
                      <SelectItem value="owner">Owner</SelectItem>
                    )}
                    {professionOptions.map((opt) => (
                      <SelectItem key={opt} value={opt.toLowerCase()}>
                        {opt}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {profile.profession === "owner" && (
                  <p className="text-xs text-text-gray">
                    Owner profession cannot be changed.
                  </p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Address</CardTitle>
          <CardDescription>
            Your current location and mailing address.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="street">Street Address</Label>
            <Input
              id="street"
              value={address.street_address}
              onChange={(e) =>
                setAddress({ ...address, street_address: e.target.value })
              }
              placeholder="Street Address"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Input
                id="city"
                value={address.city}
                onChange={(e) =>
                  setAddress({ ...address, city: e.target.value })
                }
                placeholder="City"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Input
                id="state"
                value={address.state}
                onChange={(e) =>
                  setAddress({ ...address, state: e.target.value })
                }
                placeholder="State"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="pincode">Pincode</Label>
              <Input
                id="pincode"
                value={address.pincode}
                onChange={(e) =>
                  setAddress({ ...address, pincode: e.target.value })
                }
                placeholder="6-digit pincode"
              />
            </div>
            <div className="space-y-2">
              <Label>Country</Label>
              <Input value="India" disabled className="bg-gray-bg/50" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5 text-sm-primary" />
            Notification Settings
          </CardTitle>
          <CardDescription>
            Control browser alerts and pop-up notifications.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4 py-2">
            <div>
              <p className="font-medium text-primary-text">Notifications</p>
              <p className="text-sm text-text-gray">
                Show browser notifications and in-app pop-up alerts.
              </p>
            </div>
            <Switch
              checked={notificationsEnabled}
              onCheckedChange={handleNotificationPreferenceChange}
            />
          </div>
        </CardContent>
      </Card>

      {/* <Card className="border-sm-border/50 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Preferences</CardTitle>
          <CardDescription>Customize your experience.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Language</Label>
              <Select
                value={profile.language}
                onValueChange={(v) => setProfile({ ...profile, language: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Hindi">हिंदी</SelectItem>
                  <SelectItem value="Tamil">தமிழ்</SelectItem>
                  <SelectItem value="Marathi">मराठी</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Timezone</Label>
              <Select
                value={profile.timezone}
                onValueChange={(v) => setProfile({ ...profile, timezone: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Asia/Kolkata">
                    India Standard Time (IST)
                  </SelectItem>
                  <SelectItem value="Asia/Dubai">
                    Gulf Standard Time (GST)
                  </SelectItem>
                  <SelectItem value="Asia/Singapore">
                    Singapore Time (SGT)
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card> */}

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2" disabled={isLoading}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Check className="h-4 w-4" />
          )}
          {isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {isCropperOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={handleCropCancel}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] max-w-xl h-[60vh] flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h4 className="text-base font-semibold">Adjust Photo</h4>
              <button
                onClick={handleCropCancel}
                className="text-gray-500 hover:text-gray-700"
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="relative flex-1 bg-gray-100 overflow-auto">
              {tempImageSrc && (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    keepSelection
                    className="max-w-full max-h-full"
                  >
                    <img
                      ref={imgRef}
                      src={tempImageSrc}
                      alt="to-crop"
                      onLoad={onImageLoad}
                      className="max-h-[45vh] max-w-full w-auto h-auto object-contain"
                    />
                  </ReactCrop>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={handleCropCancel}
                className="px-4 py-2 rounded-md border border-gray-300 text-gray-800 hover:bg-gray-50"
                type="button"
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                className="px-4 py-2 rounded-md bg-[#3B0069] text-white hover:bg-purple-700"
                type="button"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
